/**
 * Minimal 3-vector math for the engine layer.
 *
 * The engine cannot use three.js math (CLAUDE.md rule 2), so the world,
 * camera and overlays use these small pure helpers instead. Vectors are
 * immutable tuples: every operation returns a new value, which keeps them
 * safe to store directly in immutable component data.
 */

/** A 3-component vector. World units are meters; the world is Y-up. */
export type Vec3 = readonly [number, number, number];

export const VEC3_ZERO: Vec3 = [0, 0, 0];
export const VEC3_ONE: Vec3 = [1, 1, 1];

export function vec3Add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

export function vec3Sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function vec3Scale(v: Vec3, s: number): Vec3 {
  return [v[0] * s, v[1] * s, v[2] * s];
}

export function vec3Dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function vec3Cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

export function vec3Length(v: Vec3): number {
  return Math.hypot(v[0], v[1], v[2]);
}

export function vec3Distance(a: Vec3, b: Vec3): number {
  return vec3Length(vec3Sub(a, b));
}

/** Returns a unit vector, or the zero vector unchanged (avoids NaN on degenerate input). */
export function vec3Normalize(v: Vec3): Vec3 {
  const length = vec3Length(v);
  return length === 0 ? VEC3_ZERO : vec3Scale(v, 1 / length);
}

export function vec3Lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

export function vec3Equals(a: Vec3, b: Vec3, eps = 1e-9): boolean {
  return (
    Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps && Math.abs(a[2] - b[2]) <= eps
  );
}

/**
 * Rounds each component to `decimals` places, so computed transforms don't
 * leave float noise in the Inspector. `+ 0` turns -0 into 0, so JSON and the
 * Inspector never show "-0".
 */
export function vec3Round([x, y, z]: Vec3, decimals: number): Vec3 {
  const factor = 10 ** decimals;
  const r = (n: number) => Math.round(n * factor) / factor + 0;
  return [r(x), r(y), r(z)];
}
